import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Tag, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_PRICING_TEMPLATES, GET_ALL_ORGANIZATIONS } from '../graphql/queries';
import { CREATE_PRICING_TEMPLATE, UPDATE_PRICING_TEMPLATE, DELETE_PRICING_TEMPLATE } from '../graphql/mutations';

const { Option } = Select;

export const Pricing: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [pricingMode, setPricingMode] = useState<'HOURLY' | 'FLAT'>('HOURLY');
  const [form] = Form.useForm();

  const { loading, data, refetch } = useQuery(GET_ALL_PRICING_TEMPLATES);
  const { data: orgsData } = useQuery(GET_ALL_ORGANIZATIONS);

  const [createPricingTemplate] = useMutation(CREATE_PRICING_TEMPLATE);
  const [updatePricingTemplate] = useMutation(UPDATE_PRICING_TEMPLATE);
  const [deletePricingTemplate] = useMutation(DELETE_PRICING_TEMPLATE);

  // Auto-select organization if user only has access to one
  useEffect(() => {
    if (orgsData?.getAllOrganizations?.length === 1 && !editingTemplate) {
      form.setFieldsValue({ organizationId: orgsData.getAllOrganizations[0].id });
    }
  }, [orgsData, editingTemplate, form]);

  const handleAdd = () => {
    setEditingTemplate(null);
    form.resetFields();
    setPricingMode('HOURLY');
    form.setFieldsValue({
      currency: 'AED',
      firstHourFree: false,
      pricingMode: 'HOURLY',
      nightStartHour: 23,
      dayStartHour: 6,
      // Auto-select organization if only one available
      organizationId: orgsData?.getAllOrganizations?.length === 1
        ? orgsData.getAllOrganizations[0].id
        : undefined,
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingTemplate(record);
    setPricingMode(record.pricingMode || 'HOURLY');
    form.setFieldsValue({
      ...record,
      organizationId: record.organizationId,
      pricingMode: record.pricingMode || 'HOURLY',
      nightStartHour: record.nightStartHour ?? 23,
      dayStartHour: record.dayStartHour ?? 6,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePricingTemplate({ variables: { id } });
      message.success('Pricing template deleted successfully');
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Remove undefined values and prepare input
      const input: any = {
        name: values.name,
        baseRate: values.baseRate,
        currency: values.currency,
        firstHourFree: values.firstHourFree,
        maxDailyRate: values.maxDailyRate,
        pricingMode: values.pricingMode,
        flatDayRate: values.flatDayRate,
        flatNightRate: values.flatNightRate,
        nightStartHour: values.nightStartHour,
        dayStartHour: values.dayStartHour,
        enabled: values.enabled !== false,
      };

      if (editingTemplate) {
        // Don't send organizationId on update (can't change organization)
        await updatePricingTemplate({ variables: { id: editingTemplate.id, input } });
        message.success('Pricing template updated successfully');
      } else {
        // Include organizationId on create
        input.organizationId = values.organizationId;
        await createPricingTemplate({ variables: { input } });
        message.success('Pricing template created successfully');
      }

      setIsModalVisible(false);
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Organization',
      key: 'organization',
      render: (_: any, record: any) => record.organization?.name || '-',
    },
    {
      title: 'Mode',
      dataIndex: 'pricingMode',
      key: 'pricingMode',
      render: (mode: string) => (
        <Tag color={mode === 'FLAT' ? 'blue' : 'default'}>{mode || 'HOURLY'}</Tag>
      ),
    },
    {
      title: 'Rate',
      key: 'rate',
      render: (_: any, record: any) => {
        if (record.pricingMode === 'FLAT') {
          return (
            <span>
              Day: {record.currency} {record.flatDayRate?.toFixed(2) || '0.00'}<br />
              Night: {record.currency} {record.flatNightRate?.toFixed(2) || '0.00'}
            </span>
          );
        }
        return `${record.currency} ${record.baseRate?.toFixed(2)}/hr`;
      },
    },
    {
      title: 'Max Daily',
      dataIndex: 'maxDailyRate',
      key: 'maxDailyRate',
      render: (rate: number, record: any) => {
        if (record.pricingMode === 'FLAT') return '-';
        return rate ? `${record.currency} ${rate.toFixed(2)}` : '-';
      },
    },
    {
      title: 'First Hour Free',
      dataIndex: 'firstHourFree',
      key: 'firstHourFree',
      render: (free: boolean, record: any) => {
        if (record.pricingMode === 'FLAT') return '-';
        return free ? 'Yes' : 'No';
      },
    },
    {
      title: 'Assignments',
      dataIndex: 'assignmentCount',
      key: 'assignmentCount',
      render: (count: number) => (
        <Tag color={count > 0 ? 'green' : 'default'}>{count} location{count !== 1 ? 's' : ''}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>{enabled ? 'Active' : 'Disabled'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm
            title="Delete this pricing template?"
            description={record.assignmentCount > 0 ? `This template is assigned to ${record.assignmentCount} location(s). Remove assignments first.` : undefined}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.assignmentCount > 0}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              disabled={record.assignmentCount > 0}
              title={record.assignmentCount > 0 ? 'Remove all assignments first' : undefined}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h1>Pricing Templates</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Pricing Template
        </Button>
      </div>
      <p style={{ marginBottom: 16, color: '#666' }}>
        Create reusable pricing templates here. Assign them to locations in the Parkings page.
      </p>
      <Table
        loading={loading}
        dataSource={data?.getAllPricingTemplates || []}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingTemplate ? 'Edit Pricing Template' : 'Add Pricing Template'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          {/* Organization selector - only show when creating and multiple orgs available */}
          {!editingTemplate && (orgsData?.getAllOrganizations?.length > 1) && (
            <Form.Item
              label="Organization"
              name="organizationId"
              rules={[{ required: true, message: 'Please select an organization' }]}
            >
              <Select placeholder="Select organization">
                {orgsData?.getAllOrganizations?.map((org: any) => (
                  <Option key={org.id} value={org.id}>{org.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Show organization name when editing (read-only) */}
          {editingTemplate && (
            <Form.Item label="Organization">
              <Input value={editingTemplate.organization?.name} disabled />
            </Form.Item>
          )}

          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter a name' }]}>
            <Input placeholder="e.g., Standard Hourly, Event Special, Weekend Rate" />
          </Form.Item>

          <Form.Item label="Currency" name="currency" rules={[{ required: true }]}>
            <Select>
              <Option value="AED">AED</Option>
              <Option value="USD">USD</Option>
              <Option value="EUR">EUR</Option>
            </Select>
          </Form.Item>

          <Divider>Pricing Mode</Divider>

          <Form.Item label="Pricing Mode" name="pricingMode" rules={[{ required: true }]}>
            <Select onChange={(value: 'HOURLY' | 'FLAT') => setPricingMode(value)}>
              <Option value="HOURLY">Hourly (per hour rate)</Option>
              <Option value="FLAT">Flat Rate (day/night fixed rates)</Option>
            </Select>
          </Form.Item>

          {pricingMode === 'HOURLY' && (
            <>
              <Form.Item
                label="Base Rate (per hour)"
                name="baseRate"
                rules={[{ required: true, message: 'Please enter a base rate' }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item label="Max Daily Rate" name="maxDailyRate">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Optional - caps daily charge" />
              </Form.Item>

              <Form.Item label="First Hour Free" name="firstHourFree">
                <Select>
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>
            </>
          )}

          {pricingMode === 'FLAT' && (
            <>
              <Form.Item
                label="Day Rate (flat)"
                name="flatDayRate"
                rules={[{ required: true, message: 'Day rate is required for flat pricing' }]}
                tooltip="Fixed rate for sessions during daytime"
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Night/Overnight Rate (flat)"
                name="flatNightRate"
                rules={[{ required: true, message: 'Night rate is required for flat pricing' }]}
                tooltip="Fixed rate for sessions during night hours or overnight"
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>

              <Space style={{ width: '100%' }} size="middle">
                <Form.Item
                  label="Night Starts At"
                  name="nightStartHour"
                  tooltip="Hour when night pricing begins (0-23)"
                  style={{ flex: 1 }}
                >
                  <Select>
                    {Array.from({ length: 24 }, (_, i) => (
                      <Option key={i} value={i}>{i.toString().padStart(2, '0')}:00</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Day Starts At"
                  name="dayStartHour"
                  tooltip="Hour when day pricing begins (0-23)"
                  style={{ flex: 1 }}
                >
                  <Select>
                    {Array.from({ length: 24 }, (_, i) => (
                      <Option key={i} value={i}>{i.toString().padStart(2, '0')}:00</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Space>

              {/* Hidden baseRate field with default value for FLAT mode */}
              <Form.Item name="baseRate" hidden initialValue={0}>
                <InputNumber />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};
